import axios from 'axios'
import { store } from '@/store'
import { logout } from '@/features/auth/authSlice'
import { normalizeErrors } from '@/utils/jsonapi'
import { isPublicPath, markSessionExpired, SESSION_EXPIRED_REASON } from '@/features/auth/sessionExpiry'

/**
 * How long a 401 waits for the in-app redirect to /login before falling back
 * to a full page load (a page outside ProtectedRoute has nothing to redirect it).
 */
const SOFT_REDIRECT_GRACE_MS = 1000
let isLoginFallbackPending = false

/**
 * Axios client instance with tenant context, auth, and JSON:API interceptors
 * Automatically normalizes JSON:API responses to flat format
 * @type {import('axios').AxiosInstance}
 */
const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  timeout: 30000,
  withCredentials: true,  // Enable sending/receiving cookies for session authentication
  xsrfCookieName: 'csrftoken',  // Django's CSRF cookie name
  xsrfHeaderName: 'X-CSRFToken',  // Django's CSRF header name
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/vnd.api+json, application/json'
  }
})

/**
 * Axios reports a timeout as ECONNABORTED ("timeout of Nms exceeded") or, with
 * clarifyTimeoutError, ETIMEDOUT. A user abort is also ECONNABORTED, so the
 * message tells the two apart.
 * @param {Object} error
 * @returns {boolean}
 */
export function isTimeoutError(error) {
  if (error?.response) return false
  if (error?.code === 'ETIMEDOUT') return true
  return error?.code === 'ECONNABORTED' && /timeout/i.test(error?.message || '')
}

/**
 * Request interceptor to add tenant ID, auth token, API key, and JSON:API includes
 */
apiClient.interceptors.request.use(
  (config) => {
    const state = store.getState()
    const { currentTenantId, isAuthenticated } = state.auth

    // Add tenant ID to header if available (unless using API key)
    // API key auth sets tenant automatically based on the key's tenant
    if (currentTenantId && !config.headers['X-API-Key'] && config.headers['X-Tenant-ID'] === undefined) {
      config.headers['X-Tenant-ID'] = currentTenantId
    }

    // Add auth token if authenticated
    // Note: Token will be added in Task 5.7 when OAuth is implemented
    if (isAuthenticated) {
      // TODO: Add token from auth state when OAuth is implemented
      // config.headers.Authorization = `Bearer ${token}`
    }

    // Support for API key authentication (for testing/scripts)
    // Can be set via: apiClient.defaults.headers.common['X-API-Key'] = 'gsk_...'
    // Or per-request: apiClient.get('/metrics/', { headers: { 'X-API-Key': 'gsk_...' } })
    // API key automatically provides both authentication and tenant context

    // Add ?include=all by default for JSON:API responses (unless already specified)
    // This loads all configured relationships to avoid additional requests
    // Individual API modules can override by passing include: null or include: ['specific', 'fields']
    if (!config.params) {
      config.params = {}
    }

    // Only add include=all if:
    // 1. No include parameter already specified
    // 2. Not explicitly disabled (include: null or include: false)
    if (config.params.include === undefined) {
      config.params.include = 'all'
    } else if (config.params.include === null || config.params.include === false) {
      // Allow disabling includes by passing null or false
      delete config.params.include
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

/**
 * Response interceptor for error handling
 * Does NOT automatically normalize - preserves meta, links, etc.
 * API modules can opt-in to normalization using jsonapi utils
 */
apiClient.interceptors.response.use(
  (response) => {
    // Keep original response intact
    // Normalization is opt-in per API module
    return response
  },
  (error) => {
    if (error.response) {
      const { status, data } = error.response

      // Normalize errors using the smart normalizeErrors utility
      const normalizedErrors = normalizeErrors(error.response)
      const errorMessage = normalizedErrors.length > 0
        ? normalizedErrors[0]?.detail
        : error.message

      // Handle specific error cases (log to console only, don't crash)
      switch (status) {
        case 400:
          // Bad request - could be tenant_required or validation
          console.error('Bad request:', errorMessage)
          break

        case 401: {
          // Unauthorized - session expired or invalid
          console.error('Unauthorized access - session may be expired')

          // Already signed out, or the boot /auth/me check is still out: the
          // auth state decides, and ProtectedRoute makes the one in-app
          // redirect. Acting here too raced it (a shell request's 401 beating
          // /auth/me's) into a second, hard redirect.
          const auth = store.getState()?.auth
          if (auth && !(auth.isAuthenticated && auth.isSessionVerified)) break

          // Don't redirect for auth endpoints where 401 is expected
          const requestUrl = error.config?.url || ''
          const authEndpoints = ['/auth/login', '/auth/magic-link/', '/auth/me', '/auth/mfa/']
          const isAuthEndpoint = authEndpoints.some((ep) => requestUrl.includes(ep))

          // Don't redirect if already on a public page
          const currentPath = window.location.pathname
          const isPublicPage = isPublicPath(currentPath)

          if (!isAuthEndpoint && !isPublicPage) {
            // E05: say why on the login page, and redirect in-app. Clearing
            // the auth state (no API call) makes ProtectedRoute send the user
            // to /login with this path as `state.from`, without a full reload.
            // The page still unmounts: only work already kept in a draft store
            // (the report builder's sessionStorage draft) survives.
            markSessionExpired()
            store.dispatch(logout())

            const returnUrl = window.location.pathname + window.location.search
            if (!isLoginFallbackPending) {
              isLoginFallbackPending = true
              setTimeout(() => {
                isLoginFallbackPending = false
                if (window.location.pathname !== currentPath) return
                window.location.replace(
                  `/login?reason=${SESSION_EXPIRED_REASON}&redirect=` + encodeURIComponent(returnUrl)
                )
              }, SOFT_REDIRECT_GRACE_MS)
            }
          }
          break
        }

        case 403:
          // Check for plan limit exceeded errors
          if (normalizedErrors[0]?.code === 'limit_exceeded') {
            console.warn('Plan limit exceeded:', errorMessage)
            window.dispatchEvent(new CustomEvent('limit-exceeded', {
              detail: normalizedErrors[0]
            }))
          } else {
            console.error('Forbidden - insufficient permissions')
          }
          break

        case 404:
          // Not found
          console.error('Resource not found')
          break

        case 422:
          // Validation error
          console.error('Validation error:', errorMessage)
          break

        case 500:
          // Server error
          console.error('Server error - please try again later')
          break

        default:
          console.error(`API error (${status}):`, errorMessage)
      }

      // Enhance error object with normalized errors and message
      error.apiError = {
        status,
        code: normalizedErrors[0]?.code || data?.code || data?.error,
        message: errorMessage,
        errors: normalizedErrors,
        details: data?.details
      }
    } else if (isTimeoutError(error)) {
      // No answer within the request's timeout. The server may still be
      // working (and may finish), so this is not a connectivity problem.
      error.apiError = {
        status: 0,
        code: 'timeout',
        isTimeout: true,
        message: 'The server is taking longer than usual to respond',
        errors: []
      }
    } else if (error.request) {
      // Network error
      console.error('Network error - unable to reach API')
      error.apiError = {
        status: 0,
        code: 'network_error',
        message: 'Network error - please check your connection',
        errors: []
      }
    }

    return Promise.reject(error)
  }
)

export default apiClient
